import { createStyles, withStyles, type WithStyles } from "@material-ui/core";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Box } from "@mui/material";
import clsx from "clsx";
import React, { type CSSProperties, type ReactNode } from "react";

type BannerAlertType = "error" | "info" | "plain" | "warning";

type BannerAlertProps = {
  children?: ReactNode;
  className?: string;
  customIcon?: ReactNode;
  style?: CSSProperties;
  type: BannerAlertType;
} & WithStyles<typeof styles>;

function BannerAlert({
  children,
  classes,
  className,
  type,
  customIcon,
  ...rest
}: BannerAlertProps) {
  const iconByType: Record<BannerAlertType, ReactNode> = {
    error: <CancelOutlinedIcon aria-hidden="true" />,
    info: <InfoOutlined aria-hidden="true" />,
    plain: <InfoOutlined aria-hidden="true" />,
    warning: <WarningAmberOutlined aria-hidden="true" />,
  };

  const classesByType: Record<BannerAlertType, string> = {
    error: classes.rootError,
    info: classes.rootInfo,
    plain: classes.rootPlain,
    warning: classes.rootWarning,
  };

  const rootClass = clsx(classesByType[type], className);

  return (
    <Box className={rootClass} component="div" {...rest}>
      {customIcon ?? iconByType[type]}
      {children}
    </Box>
  );
}

const styles = () => {
  const rootBase = {
    alignItems: "anchor-center",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "row",
    fontFamily: "Roboto",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "normal",
    gap: "8px",
    lineHeight: 1.43,
    padding: "16px 12px",
  } satisfies CSSProperties;

  const iconBase = {
    alignSelf: "flex-start",
    width: "20px",
    height: "20px",
  } satisfies CSSProperties;

  return createStyles({
    rootError: {
      ...rootBase,
      backgroundColor: "#FFEAEA",
      color: "#541313",
      "& svg": {
        ...iconBase,
        fill: "#FA3336",
      },
      "& strong": {
        fontWeight: "bold",
      },
    },
    rootInfo: {
      ...rootBase,
      backgroundColor: "#E8F4FD",
      color: "#003B5C",
      "& svg": {
        ...iconBase,
        fill: "#0288D1",
      },
      "& strong": {
        fontWeight: "bold",
      },
    },
    rootPlain: {
      ...rootBase,
      backgroundColor: "transparent",
      color: "#212121",
      "& svg": {
        ...iconBase,
        fill: "#BDBDBD",
      },
      "& strong": {
        fontWeight: "bold",
      },
    },
    rootWarning: {
      ...rootBase,
      backgroundColor: "#FFF5E6",
      color: "#5f2b01",
      "& svg": {
        ...iconBase,
        fill: "#F69B17",
      },
      "& strong": {
        fontWeight: "bold",
      },
    },
  });
};

export default withStyles(styles)(BannerAlert);
